import { create, type StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';

interface GlobalFloatingAlertsSlice {
  fromValidateEmail: boolean;
  setFromValidateEmail: (value: boolean) => void;
  resetGlobalFloatingAlerts: () => void;
}

interface MenuSlice {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
}

interface OrderSlice {
  isProcessing: boolean;
  startProcessing: () => void;
  stopProcessing: () => void;
  processingOrderId: string | null;
  setProcessingOrderId: (id: string | null) => void;
}

interface AppStore extends GlobalFloatingAlertsSlice, MenuSlice, OrderSlice {}

const createGlobalFloatingAlertsSlice = (
  set: StoreApi<AppStore>['setState']
): GlobalFloatingAlertsSlice => ({
  fromValidateEmail: false,
  setFromValidateEmail: (value: boolean) => set({ fromValidateEmail: value }),
  resetGlobalFloatingAlerts: () => set({ fromValidateEmail: false })
});

const createMenuSlice = (set: StoreApi<AppStore>['setState']): MenuSlice => ({
  isMenuOpen: false,
  setIsMenuOpen: (value: boolean) => set({ isMenuOpen: value })
});

const createOrderSlice = (set: StoreApi<AppStore>['setState']): OrderSlice => ({
  isProcessing: localStorage.getItem('isProcessing') === 'true',
  processingOrderId: localStorage.getItem('processingOrderId'),
  startProcessing: () => {
    localStorage.setItem('isProcessing', 'true');
    set({ isProcessing: true });
  },
  stopProcessing: () => {
    localStorage.removeItem('isProcessing');
    localStorage.removeItem('processingOrderId');
    set({ isProcessing: false, processingOrderId: null });
  },
  setProcessingOrderId: (id: string | null) => {
    if (id === null) {
      localStorage.removeItem('processingOrderId');
    } else {
      localStorage.setItem('processingOrderId', id);
    }
    set({ processingOrderId: id });
  }
});

export const useAppStore = create<AppStore>()(
  devtools(
    (set) => ({
      ...createGlobalFloatingAlertsSlice(set),
      ...createMenuSlice(set),
      ...createOrderSlice(set)
    }),
    {
      name: 'pte-express-web-store'
    }
  )
);

export const useSelectFromValidateEmail = () => useAppStore((state) => state.fromValidateEmail);
export const useResetGlobalFloatingAlerts = () =>
  useAppStore((state) => state.resetGlobalFloatingAlerts);
export const useSetFromValidateEmail = () => useAppStore((state) => state.setFromValidateEmail);
export const useSelectIsMenuOpen = () => useAppStore((state) => state.isMenuOpen);
export const useSetIsMenuOpen = () => useAppStore((state) => state.setIsMenuOpen);
